import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges
} from '@angular/core';
import {AppService} from '@shared/services/app.service';
import {DeviceDetectorService} from 'ngx-device-detector';

export interface ToolBar {
    left?: ToolBarButton[];
    right?: ToolBarButton[];
    splitMenus?: ToolBarButton[];
}

export interface ToolBarButton {
    label?: string;
    styleClass?: string;
    icon?: string;
    disabled?: boolean;
    tooltip?: string;
    level?: 'primary' | 'secondary' | 'info' | 'help' | 'success' | 'warning' | 'danger';
    rounded?: boolean;
    outlined?: boolean;
    raised?: boolean;
    confirm?: () => void;
    confirmMessage?: string;
    click?: () => void;
    enabledFn?: () => boolean; // default => true
    command?: () => void;
    separator?: boolean;
}

@Component({
    selector: 'app-tool-bar',
    template: `
        <ng-container *ngIf="isMobile && responsive;else desktopTmpl">
            <div class="flex justify-content-end">
                <p-splitButton label="Action..." icon="pi pi-bars" [model]="mobileItems"></p-splitButton>
            </div>
        </ng-container>
        <ng-template #desktopTmpl>
            <p-toolbar [styleClass]="styleClass">
                <div class="p-toolbar-group-left flex gap-3 flex-wrap">
                    <ng-container *ngFor="let item of toolbars.left">
                        <button pButton
                                type="button"
                                [label]="item.label"
                                [icon]="item.icon"
                                [class]="item.styleClass"
                                [disabled]="!item.enabledFn()"
                                pTooltip="{{item.tooltip}}"
                                (click)="click(item)"
                        ></button>
                    </ng-container>
                    <ng-content select="[left-content]"></ng-content>
                </div>

                <ng-content></ng-content>

               <ng-content></ng-content>

                <div class="p-toolbar-group-right flex gap-3 flex-wrap">
                    <ng-container *ngFor="let item of toolbars.right">
                        <button pButton
                                type="button"
                                [label]="item.label"
                                [icon]="item.icon"
                                [class]="item.styleClass"
                                [disabled]="!item.enabledFn()"
                                [pTooltip]="item.tooltip"
                                (click)="click(item)"
                        ></button>
                    </ng-container>
                  <ng-container *ngIf="toolbars?.splitMenus?.length">
                    <p-splitButton
                      [label]="splitMenusLabel"
                      icon="pi pi-bars"
                      [styleClass]="splitMenusStyle"
                      [model]="toolbars.splitMenus"
                    ></p-splitButton>
                  </ng-container>
                </div>
            </p-toolbar>
        </ng-template>
    `,
    styles: [],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ToolBarComponent implements OnInit, OnChanges {
    @Input() toolbars: ToolBar;
    @Input() changeDetectSource: any;
    @Input() responsive = false;
    @Input() splitMenusStyle = '';
    @Input() splitMenusLabel = 'More..';
    @Input() styleClass: string;
    @Input() mini = false;

    isMobile = false;
    mobileItems: any;

    constructor(private deviceDetectorService: DeviceDetectorService,
                private ref: ChangeDetectorRef,
                private app: AppService) {
    }

    ngOnInit(): void {
        this.isMobile = this.deviceDetectorService.isMobile();
        if (!this.changeDetectSource) {
            console.warn('No changeDetectSource');
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.renderAll();
    }

    private renderAll() {
        if (this.isMobile && this.responsive) {
            const items = [
                ...this.toolbars.left,
                ...this.toolbars.right,
                ...this.toolbars.splitMenus,
            ];

            items.forEach(item => {
                item.disabled = !item.enabledFn();
                item.command = () => {
                    this.click(item);
                };
            });

            this.mobileItems = items;
        } else {
            this.renderLeft();
            this.renderRight();
            this.renderSplitMenus();
        }
    }

    private renderRight() {
        (this.toolbars.right || []).forEach(btn => {
            // default => true
            btn.enabledFn = btn.enabledFn || (() => true);

            btn.styleClass = [
                btn.styleClass,
                (btn.level ? `p-button-${btn.level}` : 'p-button-secondary'),
                btn.outlined === false ? '' : 'p-button-outlined',
                btn.rounded ? 'p-button-rounded' : '',
                btn.raised ? 'p-button-raised' : '',
                this.mini ? 'p-button-sm' : ''
            ].join(' ');
        });
    }

    private renderLeft() {
        (this.toolbars.left || []).forEach(btn => {
            // default => true
            btn.enabledFn = btn.enabledFn || (() => true);

            btn.styleClass = [
                btn.styleClass,
                (btn.level ? `p-button-${btn.level}` : 'p-button-outlined'),
                btn.outlined ? 'p-button-outlined' : '',
                btn.rounded ? 'p-button-rounded' : '',
                btn.raised ? 'p-button-raised' : '',
                this.mini ? 'p-button-sm' : ''
            ].join(' ');
        });
    }

    private renderSplitMenus() {
        if (this.toolbars.splitMenus) {
            this.toolbars.splitMenus.forEach(item => {
                item.disabled = item.enabledFn ? !item.enabledFn() : false;
                item.command = () => {
                    this.click(item);
                };
            });
        }
    }

    click(item: ToolBarButton) {
        if (item.confirm) {
            this.app.confirm(
                item.confirmMessage || item.tooltip || item.label
                , () => item.confirm()
                , !!item.confirmMessage
            );
        } else if (item.click) {
            item.click();
        } else {
            console.warn('No click handler');
        }
    }

    detectChange() {
        this.renderAll();
        this.ref.markForCheck();
    }
}
